import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  Grid
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Webhook as WebhookIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { api } from '../contexts/ApiContext';
import {
  FIT_TABLE_SX,
  FIT_TABLE_CONTAINER_SX,
  FitColgroup,
  equalFitCols,
  fitTruncateSx,
  fitChipSx,
} from '../utils/fitTable';

interface WebhookData {
  id: number;
  user_id: number;
  webhook_id: string | null;
  event_type: string;
  webhook_url: string | null;
  request_id: string | null;
  headers: Record<string, any>;
  signature: string | null;
  event_id: string | null;
  event_data: Record<string, any>;
  event_timestamp: string | null;
  transaction_id: string | null;
  transaction_state: string | null;
  transaction_type: string | null;
  processed: boolean;
  processing_error: string | null;
  created_at: string;
  updated_at: string;
}

interface WebhookResponse {
  webhooks: WebhookData[];
  pagination: {
    page: number;
    per_page: number;
    total_count: number;
    total_pages: number;
  };
  event_type_stats: Record<string, number>;
  timestamp: string;
}

const Webhooks: React.FC = () => {
  const [webhookData, setWebhookData] = useState<WebhookResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [processedFilter, setProcessedFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [selectedWebhook, setSelectedWebhook] = useState<WebhookData | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [selectedEvents, setSelectedEvents] = useState<string[]>(['TransactionCreated', 'TransactionStateChanged']);
  const [revolutWebhooks, setRevolutWebhooks] = useState<any[]>([]);
  const [fetchingRevolutWebhooks, setFetchingRevolutWebhooks] = useState(false);

  const fetchRevolutWebhooks = async () => {
    setFetchingRevolutWebhooks(true);
    try {
      const response = await api.get('/api/revolut/webhooks');
      // Handle both success:true and success:false cases (backend returns 200 even on error)
      if (response.data && Array.isArray(response.data.webhooks)) {
        setRevolutWebhooks(response.data.webhooks || []);
      } else if (response.data.success === false) {
        console.warn('Failed to fetch Revolut webhooks:', response.data.error);
        setRevolutWebhooks([]); // Set empty array on error
      } else {
        setRevolutWebhooks(response.data.webhooks || []);
      }
    } catch (err: any) {
      console.error('Error fetching Revolut webhooks:', err);
      setRevolutWebhooks([]); // Set empty array on error
      // Don't show error to user - this is optional data
    } finally {
      setFetchingRevolutWebhooks(false);
    }
  };

  const fetchWebhookData = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: '50',
      });
      
      // Only add filter params if they have values (empty string means "all")
      if (eventTypeFilter && eventTypeFilter.trim() !== '') {
        params.append('event_type', eventTypeFilter);
      }
      
      if (processedFilter && processedFilter.trim() !== '') {
        params.append('processed', processedFilter);
      }
      
      if (searchFilter && searchFilter.trim() !== '') {
        params.append('search', searchFilter.trim());
      }

      const response = await api.get(`/api/admin/webhooks?${params}`);
      setWebhookData(response.data);
    } catch (err: any) {
      console.error('Error fetching webhook data:', err);
      setError(err.response?.data?.error || 'Failed to fetch webhook data');
    } finally {
      setLoading(false);
    }
  };

  const createWebhook = async () => {
    try {
      const response = await api.post('/api/revolut/webhooks/create', {
        url: webhookUrl,
        events: selectedEvents
      });
      
      if (response.data.success) {
        alert('Webhook created successfully!');
        setCreateDialogOpen(false);
        setWebhookUrl('');
        fetchWebhookData();
        fetchRevolutWebhooks(); // Refresh webhook subscriptions list
      } else {
        alert('Failed to create webhook: ' + response.data.error);
      }
    } catch (err: any) {
      console.error('Error creating webhook:', err);
      alert('Error creating webhook: ' + (err.response?.data?.error || 'Unknown error'));
    }
  };

  // Listen for sync event from parent Revolut page and fetch data
  useEffect(() => {
    const handleSyncAll = () => {
      // Refresh webhook data when parent page loads
      fetchWebhookData();
      fetchRevolutWebhooks(); // Also fetch webhook subscriptions from Revolut API
    };

    window.addEventListener('revolut-sync-all', handleSyncAll);
    
    // Fetch data on mount and when filters change
    fetchWebhookData();
    // Automatically fetch webhook subscriptions from Revolut API on page load
    fetchRevolutWebhooks();

    return () => {
      window.removeEventListener('revolut-sync-all', handleSyncAll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, eventTypeFilter, processedFilter, searchFilter]); // fetchRevolutWebhooks doesn't need to be in deps - it's stable

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-IE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getEventTypeColor = (eventType: string) => {
    switch (eventType) {
      case 'TransactionCreated':
        return 'success';
      case 'TransactionStateChanged':
        return 'primary';
      case 'PayoutLinkCreated':
        return 'secondary';
      case 'PayoutLinkStateChanged':
        return 'warning';
      default:
        return 'default';
    }
  };

  const handleViewDetails = (webhook: WebhookData) => {
    setSelectedWebhook(webhook);
    setDetailDialogOpen(true);
  };

  if (loading && !webhookData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading webhook data...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center' }}>
          <WebhookIcon sx={{ mr: 2 }} />
          Revolut Webhooks
        </Typography>
        <Box>
          {/* Only show Create Webhook button if no webhook exists (Revolut allows only one webhook) */}
          {revolutWebhooks.length === 0 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
              sx={{ mr: 2 }}
            >
              Create Webhook
            </Button>
          )}
          {revolutWebhooks.length > 0 && (
            <Alert severity="info" sx={{ mr: 2, display: 'inline-flex', alignItems: 'center' }}>
              Webhook already registered. Revolut allows only one webhook per account.
            </Alert>
          )}
          <Button
            variant="outlined"
            onClick={() => {
              fetchWebhookData();
              fetchRevolutWebhooks();
            }}
            startIcon={<RefreshIcon />}
            disabled={loading || fetchingRevolutWebhooks}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Registered Webhooks from Revolut API */}
      {revolutWebhooks.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Registered Webhooks (from Revolut API)
              </Typography>
              {fetchingRevolutWebhooks && <CircularProgress size={20} />}
            </Box>
            <Grid container spacing={2}>
              {revolutWebhooks.map((webhook: any, index: number) => (
                <Grid size={{ xs: 12, md: 6 }} key={webhook.id || index}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">Webhook ID</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>{webhook.id || 'N/A'}</Typography>
                    <Typography variant="subtitle2" color="text.secondary">URL</Typography>
                    <Typography variant="body2" sx={{ mb: 1, wordBreak: 'break-all' }}>{webhook.url || 'N/A'}</Typography>
                    <Typography variant="subtitle2" color="text.secondary">Events</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {(webhook.events || []).map((event: string) => (
                        <Chip key={event} label={event} size="small" color="primary" variant="outlined" />
                      ))}
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Statistics Cards */}
      {webhookData?.event_type_stats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {Object.entries(webhookData.event_type_stats).map(([eventType, count]) => (
            <Grid size={{ xs: 12, md: 3 }} key={eventType}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="primary">
                    {eventType.replace(/([A-Z])/g, ' $1').trim()}
                  </Typography>
                  <Typography variant="h4">
                    {count.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Events
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="Search"
                placeholder="Transaction ID, Event ID, or description..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Event Type</InputLabel>
                <Select
                  value={eventTypeFilter}
                  label="Event Type"
                  onChange={(e) => setEventTypeFilter(e.target.value)}
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="TransactionCreated">Transaction Created</MenuItem>
                  <MenuItem value="TransactionStateChanged">Transaction State Changed</MenuItem>
                  <MenuItem value="PayoutLinkCreated">Payout Link Created</MenuItem>
                  <MenuItem value="PayoutLinkStateChanged">Payout Link State Changed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={processedFilter}
                  label="Status"
                  onChange={(e) => setProcessedFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Processed</MenuItem>
                  <MenuItem value="false">Unprocessed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Webhooks Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Webhook Events ({webhookData?.pagination.total_count || 0} total)
          </Typography>
          
          <TableContainer component={Paper} variant="outlined" sx={FIT_TABLE_CONTAINER_SX}>
            <Table size="small" sx={FIT_TABLE_SX}>
              <FitColgroup
                cols={equalFitCols(
                  ['eventType', 'eventId', 'txId', 'status', 'received', 'actions'],
                  { flexKey: 'eventId' }
                )}
              />
              <TableHead>
                <TableRow>
                  <TableCell>Event Type</TableCell>
                  <TableCell>Event ID</TableCell>
                  <TableCell>Transaction ID</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Received</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {webhookData?.webhooks.map((webhook) => (
                  <TableRow key={webhook.id}>
                    <TableCell>
                      <Chip 
                        label={webhook.event_type} 
                        color={getEventTypeColor(webhook.event_type) as any}
                        size="small"
                        sx={fitChipSx}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={fitTruncateSx} title={webhook.event_id || 'N/A'}>
                        {webhook.event_id || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={fitTruncateSx} title={webhook.transaction_id || 'N/A'}>
                        {webhook.transaction_id || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {webhook.processed ? (
                        <Chip 
                          icon={<CheckCircleIcon />}
                          label="Processed" 
                          color="success" 
                          size="small" 
                          sx={fitChipSx}
                        />
                      ) : (
                        <Chip 
                          icon={<ScheduleIcon />}
                          label="Unprocessed" 
                          color="warning" 
                          size="small" 
                          sx={fitChipSx}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={fitTruncateSx}>
                        {formatDate(webhook.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell className="fit-table-no-clip">
                      <Tooltip title="View Details">
                        <IconButton 
                          size="small" 
                          onClick={() => handleViewDetails(webhook)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {webhookData?.pagination && webhookData.pagination.total_pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={webhookData.pagination.total_pages}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Webhook Details Dialog */}
      <Dialog 
        open={detailDialogOpen} 
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Webhook Event Details</DialogTitle>
        <DialogContent>
          {selectedWebhook && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Event Information
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="body2" color="text.secondary">Event Type:</Typography>
                  <Typography variant="body1">{selectedWebhook.event_type}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="body2" color="text.secondary">Event ID:</Typography>
                  <Typography variant="body1">{selectedWebhook.event_id || 'N/A'}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="body2" color="text.secondary">Transaction ID:</Typography>
                  <Typography variant="body1">{selectedWebhook.transaction_id || 'N/A'}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="body2" color="text.secondary">Status:</Typography>
                  <Typography variant="body1">
                    {selectedWebhook.processed ? 'Processed' : 'Unprocessed'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="body2" color="text.secondary">Received:</Typography>
                  <Typography variant="body1">{formatDate(selectedWebhook.created_at)}</Typography>
                </Grid>
              </Grid>

              <Typography variant="h6" sx={{ mb: 2 }}>
                Event Data
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
                <pre style={{ margin: 0, fontSize: '12px', whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(selectedWebhook.event_data, null, 2)}
                </pre>
              </Paper>

              {selectedWebhook.processing_error && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" color="error" sx={{ mb: 1 }}>
                    Processing Error
                  </Typography>
                  <Alert severity="error">
                    {selectedWebhook.processing_error}
                  </Alert>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Create Webhook Dialog */}
      <Dialog 
        open={createDialogOpen} 
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Webhook</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Webhook URL"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              placeholder="https://your-domain.com/api/revolut/webhook"
              sx={{ mb: 3 }}
            />
            <Typography variant="h6" sx={{ mb: 2 }}>
              Event Types
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Events to Subscribe</InputLabel>
              <Select
                multiple
                value={selectedEvents}
                label="Events to Subscribe"
                onChange={(e) => setSelectedEvents(e.target.value as string[])}
              >
                <MenuItem value="TransactionCreated">Transaction Created</MenuItem>
                <MenuItem value="TransactionStateChanged">Transaction State Changed</MenuItem>
                <MenuItem value="PayoutLinkCreated">Payout Link Created</MenuItem>
                <MenuItem value="PayoutLinkStateChanged">Payout Link State Changed</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={createWebhook} 
            variant="contained"
            disabled={!webhookUrl || selectedEvents.length === 0}
          >
            Create Webhook
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Webhooks;
